import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { UnifiedAuthProvider, useUnifiedAuth } from './context/UnifiedAuthContext';
import { CourseProvider } from './context/CourseContext';
import { TemplateProvider } from './context/TemplateContext';
import { InstanceProvider } from './context/InstanceContext';
import { ThemeProvider } from './context/ThemeContext';
import { USER_ROLES } from './config/userRoles';
import { ToastContainer } from './components/ui/ToastContainer';
import Welcome from './views/welcome';
import NotFound from './views/NotFound';
// Removed: AdminView (legacy) - use LiveModeView at /live/:instanceId instead
import QuestionSetView from './views/questionSetView';
import LLMGeneratorView from './views/LLMGeneratorView3';
import DatabaseManagerView from './views/databaseManagerView';
// Removed: DashboardView (legacy) - use DashboardView2
import DashboardView2 from './views/DashboardView2';
// Removed: CourseView (legacy) - use CourseView2
import CourseView2 from './views/CourseView2';
// import TemplateEditorView from './views/templateEditorView'; // Legacy - removed
import TemplateEditorView2 from './views/TemplateEditorView2';
import LiveModeView from './views/liveModeView';
import ResultsView from './views/resultsView';
import { StudentCompleteLogin, StudentDashboard, JoinCourse, StudentQuizHistory, PracticeQuizView } from './views/student';
// Removed: VerifyEmail (old email verification system - now using magic link)
import { CompleteLogin } from './views/auth';
import { AdminDashboard } from './views/admin';
import AdminDashboardV2 from './views/admin/AdminDashboardV2';
import BugReportManagement from './views/admin/BugReportManagement';

// New modular quiz participant view
import { QuizParticipantView } from './components/quiz-participant';

// Admin-only Route wrapper
function AdminRoute({ children }) {
  const { userProfile, loading, initializing, user } = useUnifiedAuth();
  const location = useLocation();
  
  // Show loading while checking auth state or initializing
  if (loading || initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Überprüfe Berechtigung...</p>
        </div>
      </div>
    );
  }
  
  // Not authenticated - redirect to welcome with auth modal
  if (!user) {
    const redirectPath = encodeURIComponent(location.pathname);
    return <Navigate to={`/?auth=true&redirect=${redirectPath}`} replace />;
  }
  
  // Not admin - redirect to appropriate dashboard based on role
  if (userProfile?.role !== USER_ROLES.ADMIN) {
    if (userProfile?.role === USER_ROLES.STUDENT) {
      return <Navigate to="/student/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Teacher/Admin only Route wrapper (excludes students)
function TeacherRoute({ children }) {
  const { userProfile, loading, initializing, user } = useUnifiedAuth();
  const location = useLocation();
  
  if (loading || initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Lade...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    const redirectPath = encodeURIComponent(location.pathname);
    return <Navigate to={`/?auth=true&redirect=${redirectPath}`} replace />;
  }
  
  // Students can't access teacher routes
  if (userProfile?.role === USER_ROLES.STUDENT) {
    return <Navigate to="/student/dashboard" replace />;
  }
  
  return children;
}

// Role-based redirect after login
function RoleBasedRedirect() {
  const { userProfile, loading, initializing } = useUnifiedAuth();
  
  if (loading || initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Lade Dashboard...</p>
        </div>
      </div>
    );
  }
  
  // Redirect based on role
  if (userProfile?.role === USER_ROLES.STUDENT) {
    return <Navigate to="/student/dashboard" replace />;
  } else if (userProfile?.role === USER_ROLES.ADMIN) {
    return <Navigate to="/admin/dashboard" replace />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
}

// Placeholder components for future phases

function App() {
  return (
    <ThemeProvider>
      <UnifiedAuthProvider>
        <CourseProvider>
          <TemplateProvider>
            <InstanceProvider>
              <Router>
                  <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors">
                    <Routes>
                      {/* ========================================
                          CORE ROUTES
                          ======================================== */}
                      <Route path="/" element={<Welcome />} />
                      <Route path="/editor" element={<QuestionSetView />} />
                      <Route path="/editor/:sessionId" element={<QuestionSetView />} />
                      <Route path="/llm-generator" element={
                        <TeacherRoute>
                          <LLMGeneratorView />
                        </TeacherRoute>
                      } />
                      <Route path="/database" element={
                        <AdminRoute>
                          <DatabaseManagerView />
                        </AdminRoute>
                      } />
                      
                      {/* ========================================
                          NEW MODULAR QUIZ PARTICIPANT (Phase 4)
                          ======================================== */}
                      <Route path="/play/:code" element={<QuizParticipantView />} />
                      
                      {/* ========================================
                          UNIFIED AUTH ROUTES (Magic Link for all)
                          ======================================== */}
                      <Route path="/auth/complete-login" element={<CompleteLogin />} />
                      
                      {/* ========================================
                          ADMIN ROUTES
                          ======================================== */}
                      <Route path="/admin/dashboard" element={
                        <AdminRoute>
                          <AdminDashboardV2 />
                        </AdminRoute>
                      } />
                      <Route path="/admin/dashboard-old" element={<AdminDashboard />} />
                      <Route path="/admin/bug-reports" element={
                        <AdminRoute>
                          <BugReportManagement />
                        </AdminRoute>
                      } />
                      
                      {/* ========================================
                          STUDENT ROUTES (Magic Link Auth)
                          ======================================== */}
                      <Route path="/student/complete-login" element={<StudentCompleteLogin />} />
                      <Route path="/student/dashboard" element={<StudentDashboard />} />
                      <Route path="/student/history" element={<StudentQuizHistory />} />
                      <Route path="/student/practice/:sessionId?" element={<PracticeQuizView />} />
                      {/* Removed: /student/verify/:verificationCode - old email verification, now using magic link */}
                      <Route path="/join/:inviteCode" element={<JoinCourse />} />
                    
                    {/* ========================================
                        NEW MULTI-USER ROUTES (Phase 1+)
                        ======================================== */}
                  
                  {/* Phase 1-2: Dashboard & Courses - Teacher/Admin only */}
                  <Route path="/dashboard" element={
                    <TeacherRoute>
                      <DashboardView2 />
                    </TeacherRoute>
                  } />
                  {/* Removed: /dashboard-old route - legacy DashboardView archived */}
                  <Route path="/course/:courseId" element={<CourseView2 />} />
                  {/* Removed: /courses/:courseId route - legacy CourseView archived */}
                  
                  {/* Phase 2-3: Templates - Teacher/Admin only */}
                  <Route path="/template/new" element={
                    <TeacherRoute>
                      <TemplateEditorView2 />
                    </TeacherRoute>
                  } />
                  <Route path="/template/:templateId" element={
                    <TeacherRoute>
                      <TemplateEditorView2 />
                    </TeacherRoute>
                  } />
                  <Route path="/template/:templateId/edit" element={
                    <TeacherRoute>
                      <TemplateEditorView2 />
                    </TeacherRoute>
                  } />
                  
                  {/* Phase 5: Live Control Mode */}
                  <Route path="/live/:instanceId" element={<LiveModeView />} />
                  
                  {/* Phase 6: Results & Analytics */}
                  <Route path="/results/:instanceId" element={<ResultsView />} />
                  
                  {/* Fallback redirects */}
                  <Route path="/admin" element={<QuestionSetView />} />
                  <Route path="/questions" element={<QuestionSetView />} />
                  
                  {/* 404 - Catch all non-existent routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
            </Router>
            <ToastContainer position="top-right" />
          </InstanceProvider>
        </TemplateProvider>
      </CourseProvider>
    </UnifiedAuthProvider>
  </ThemeProvider>
  );
}

export default App;
